import { useState } from "react";
import { subscriptionService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState, useDebounce } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import { buildSearchQuery, createUUID } from "@miplatform/utils";

const getUserUUID = createUUID();
const updateUserUUID = createUUID();
const getRolesUUID = createUUID();
const getTitleUUID = createUUID();
const resendInvitationUUID = createUUID();

export const useManageUserViewModel = ({ tenantId }) => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [titles, setTitles] = useState([]);
  const [search, setSearch] = useState("");
  const stateManager = useComponentState();
  const [drawer, setDrawer] = useState({});
  const debouncedSearch = useDebounce(search, 500);

  const getUsers = (search) => {
    stateManager.abort(getUserUUID);
    stateManager.run(async () => {
      const searchQuery = buildSearchQuery(search, [
        "firstName",
        "lastName",
        "userName",
        "displayName",
        "email",
      ]);
      const response = await subscriptionService.users.getAll({
        filters: searchQuery,
        sort: "firstName",
      });
      if (response.status === HTTP_STATUSES.OK) {
        setUsers(response.data);
      }
    }, getUserUUID);
  };

  const onCreateUser = async (data) => {
    stateManager.abort(updateUserUUID);
    return stateManager.run(async () => {
      const response = await subscriptionService.users.createUser({
        data: { ...data, tenantId },
        toastMessageConfig: {
          success: {
            text: `User was created successfully.`,
          },
          failure: {
            text: `Failed to create user.`,
          },
        },
      });
      setDrawer({});
      if (response?.status !== HTTP_STATUSES.OK) {
        return;
      }
      getUsers();
      toastService.success({
        text: `Invitation email sent successfully to ${data.email}. Please check your email.`,
      });
    }, updateUserUUID);
  };

  const getTitles = () => {
    stateManager.abort(getTitleUUID);
    stateManager.run(async () => {
      const response = await subscriptionService.titles.getAll();
      if (response.status === HTTP_STATUSES.OK) {
        setTitles(
          response.data.map((title) => ({ label: title.name, value: title.id }))
        );
      }
    }, getTitleUUID);
  };

  const getRoles = () => {
    stateManager.abort(getRolesUUID);
    stateManager.run(async () => {
      const response = await subscriptionService.roles.getAll({
        sort: "name",
      });
      if (response.status === HTTP_STATUSES.OK) {
        const mappedRoles = response.data?.map((item) => ({
          label: item.name,
          value: item.id,
        }));
        setRoles(mappedRoles);
      }
    }, getRolesUUID);
  };

  const resendInvitation = (user) => {
    const { id, email } = user;
    stateManager.abort(resendInvitationUUID);
    stateManager.run(() => {
      return subscriptionService.users.resendInvitation({
        id,
        toastMessageConfig: {
          success: {
            text: `Invitation resent successfully to ${email}.`,
          },
          failure: {
            text: "Failed to resend invitation. Please try again later.",
          },
        },
      });
    });
  };

  const getChipColor = (status) => {
    switch (status) {
      case "Active":
        return "success";
      case "Disabled":
        return "error";
      case "Pending":
        return "secondary";
      default:
        return "primary";
    }
  };

  const loadData = () => {
    getUsers();
    getRoles();
  };

  const onSearch = (debouncedSearch) => {
    if (debouncedSearch?.length > 1) {
      getUsers(debouncedSearch);
    } else {
      getUsers();
    }
  };

  return {
    users,
    roles,
    titles,
    stateManager,
    search,
    drawer,
    debouncedSearch,
    getTitles,
    setSearch,
    resendInvitation,
    setDrawer,
    onCreateUser,
    getChipColor,
    loadData,
    onSearch,
  };
};
